// Lib
import React, { ComponentType, ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDuplicateLoadingForCurrentBoard } from '../../element/duplicate/elementDuplicateSelector';
import { hasRetrievedAllAncestorsSelector } from '../../utils/permissions/elementPermissionsSelector';
import { getMilanoteApplicationModeSelector } from '../../platform/platformSelector';

import {
    fetchBoard,
    refreshIfModified,
    FetchBoardArgs,
    RefreshBoardIfModifiedArgs,
} from '../../element/board/boardService';
import { fetchBoardsFromLocalCache } from '../../element/board/boardCacheService';
import usePrevious from '../../utils/react/usePrevious';
import useUpdateEffect from '../../utils/react/useUpdateEffect';
import { MilanoteApplicationMode } from '../../../common/platform/platformTypes';

interface WorkspaceCurrentBoardFetchDecoratorProps {
    currentBoardId: string;
    isCurrentBoardFetched: boolean;
    isCurrentBoardCached: boolean;
    isCurrentBoardFetching: boolean;
    isCurrentBoardParentFetched: boolean;
    socketConnected: boolean;
    isGuest: boolean;
}

/**
 * This component handles fetching the current board and its children when:
 *  - The page loads,
 *  - The current board changes
 *  - The current board has not been fetched due to an error fetching it
 *  - The socket was disconnected but has now reconnected
 */
const workspaceCurrentBoardFetchDecorator = (DecoratedComponent: ComponentType<any>) => {
    return function workspaceCurrentBoardFetchDecorator(props: WorkspaceCurrentBoardFetchDecoratorProps): ReactElement {
        const {
            currentBoardId,
            isCurrentBoardFetched,
            isCurrentBoardCached,
            isCurrentBoardFetching,
            socketConnected,
            isGuest,
        } = props;

        const isCurrentBoardDuplicating = useSelector(getDuplicateLoadingForCurrentBoard);
        const areCurrentBoardAncestorsFetched = useSelector(hasRetrievedAllAncestorsSelector);
        const appMode = useSelector(getMilanoteApplicationModeSelector);

        const dispatch = useDispatch();
        const dispatchFetchBoard = (args: FetchBoardArgs) => dispatch(fetchBoard(args));
        const dispatchFetchBoardIfModified = (args: RefreshBoardIfModifiedArgs) => dispatch(refreshIfModified(args));
        const dispatchFetchBoardFromLocalCache = (boardIds: string[]) => dispatch(fetchBoardsFromLocalCache(boardIds));

        const shouldFetchCanvasOrder = appMode === MilanoteApplicationMode.mobileLegacy;

        const prevCurrentBoardId = usePrevious(currentBoardId);
        const prevSocketConnected = usePrevious(socketConnected);
        const prevIsCurrentBoardCached = usePrevious(isCurrentBoardCached);

        useEffect(() => {
            if (!currentBoardId) return;

            // Fetch the current board when the page loads
            dispatchFetchBoard({
                boardId: currentBoardId,
                loadAncestors: true,
                canvasOrder: shouldFetchCanvasOrder,
            });
        }, []);

        useEffect(() => {
            if (!currentBoardId) return;

            // If the socket has reconnected, only fetch the board if it's been modified in the meantime.
            // If we don't check for modification, the board won't be fetched because the "boards resource" will
            // think it's already been fetched
            if (socketConnected && !prevSocketConnected) {
                dispatchFetchBoardIfModified({
                    boardId: currentBoardId,
                    loadAncestors: true,
                    canvasOrder: shouldFetchCanvasOrder,
                });
            }
        }, [socketConnected, prevSocketConnected, currentBoardId]);

        useUpdateEffect(() => {
            const shouldReFetchBoard = () => {
                // If we don't have a current board ID, don't fetch anything
                if (!currentBoardId) return false;

                // If we're not connected to the socket, and the user is not a guest, _and_ it's not the first render,
                if (!socketConnected && !isGuest) return false;

                // If the current board is in the process of being duplicated, then don't fetch
                if (isCurrentBoardDuplicating) return false;

                // Handles the case that the current board is being fetched, but then errors during the fetch,
                // but has not been marked as being fetched due to the helper service
                if (isCurrentBoardFetched || isCurrentBoardFetching) return false;

                return currentBoardId !== prevCurrentBoardId;
            };

            if (shouldReFetchBoard()) {
                // load ancestors if the full list of ancestors are not available,
                // or if the current boardId has only just become available
                const initialFetch = !prevCurrentBoardId;
                const loadAncestors = !areCurrentBoardAncestorsFetched || initialFetch;

                // If the current board _has_ been fetched, but it's ancestors haven't,
                // then force the fetch to ensure that ancestors are loaded
                const force = !areCurrentBoardAncestorsFetched && isCurrentBoardFetched;

                dispatchFetchBoard({
                    boardId: currentBoardId,
                    force,
                    loadAncestors,
                    canvasOrder: shouldFetchCanvasOrder,
                });
                return;
            }

            const shouldFetchBoardFromLocalCache = () => {
                if (!isCurrentBoardCached) return false;

                // If we've changed to a new board, fetch it from the cache
                if (currentBoardId !== prevCurrentBoardId) return true;

                // If the current board wasn't cached but is now, fetch it from the cache
                if (!prevIsCurrentBoardCached) return true;
            };

            // If we've changed to a pre-fetched board, ensure that it gets loaded from the cache
            if (shouldFetchBoardFromLocalCache()) {
                dispatchFetchBoardFromLocalCache([currentBoardId]);
            }
        }, [
            isGuest,
            socketConnected,
            currentBoardId,
            isCurrentBoardFetched,
            isCurrentBoardCached,
            isCurrentBoardFetching,
            areCurrentBoardAncestorsFetched,
        ]);

        return <DecoratedComponent {...props} />;
    };
};

export default workspaceCurrentBoardFetchDecorator;
